import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Renders a sticky clone of the table header when the user scrolls the document.
 * This evades the `overflow-x: auto` limitation for `position: sticky`.
 */
export function StickyTableHeadersOverlay({ editor, topOffset = 60 }) {
    const [activeTable, setActiveTable] = useState(null);
    const animationFrameRef = useRef(null);

    // Helper to find the dynamic top boundary of the scrolling container
    const getDynamicTop = (wrapper) => {
        // 1. Get the scroll container's physical top
        let containerTop = 0;
        let current = wrapper;
        while (current && current !== document.body && current !== document.documentElement) {
            const style = window.getComputedStyle(current);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                containerTop = Math.max(0, current.getBoundingClientRect().top);
                break;
            }
            current = current.parentElement;
        }

        // 2. Find the bottom edge of any visible overlapping headers
        let maxStickyBottom = 0;
        const selectors = ['.doc-top-bar', '.task-hoy-toolbar', '.wf-doc-header', '#header'];
        
        selectors.forEach(sel => {
            const els = document.querySelectorAll(sel);
            els.forEach(el => {
                const rect = el.getBoundingClientRect();
                // If the header is visible and at the top portion of the screen
                if (rect.height > 0 && rect.top >= 0 && rect.top <= Math.max(150, containerTop)) {
                    maxStickyBottom = Math.max(maxStickyBottom, rect.bottom);
                }
            });
        });
        
        // Return the lowest boundary to ensure the table header never overlaps them
        return Math.max(containerTop, maxStickyBottom);
    };

    useEffect(() => {
        if (!editor || !editor.view || !editor.view.dom) return;
        const editorDom = editor.view.dom;

        const updateSticky = () => {
            let found = null;
            const tables = Array.from(editorDom.querySelectorAll('table'));
            
            for (const table of tables) {
                // Tiptap native tables have .tableWrapper, but imported/pasted ones might not
                const wrapper = table.closest('.tableWrapper') || table.closest('.doctex-rich-editor') || table;
                const rect = wrapper.getBoundingClientRect();

                const headerRow = table.querySelector('tr');
                if (!headerRow) continue;

                const headerHeight = headerRow.offsetHeight;
                
                // Get the lowest visible boundary of headers or scroll container
                const dynamicTop = getDynamicTop(wrapper);
                
                // If the top of the table has scrolled ABOVE the dynamic offset
                // AND the bottom of the table is still BELOW the offset + header height
                if (rect.top <= dynamicTop && rect.bottom > dynamicTop + headerHeight) {
                    const ths = Array.from(headerRow.querySelectorAll('th, td'));
                    const widths = ths.map(th => th.getBoundingClientRect().width);
                    
                    found = {
                        tableWidth: table.getBoundingClientRect().width,
                        wrapperWidth: rect.width, // wrapper width bounds the visible sticky header
                        left: rect.left,
                        scrollLeft: wrapper.scrollLeft || 0,
                        top: dynamicTop,
                        ths: ths.map((th, i) => ({
                            html: th.innerHTML,
                            width: widths[i],
                            className: th.className,
                            style: th.getAttribute('style') || ''
                        }))
                    };
                    break;
                }
            }
            
            setActiveTable(found);
        };

        const onEvent = () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = requestAnimationFrame(updateSticky);
        };

        // eslint-disable-next-line no-restricted-syntax
        window.addEventListener('scroll', onEvent, { passive: true });
        // eslint-disable-next-line no-restricted-syntax
        window.addEventListener('resize', onEvent, { passive: true });
        // Use capture phase to catch scroll events from any scrollable children
        document.addEventListener('scroll', onEvent, true); 
        
        // Watch for table column resizes (style changes) or structural changes
        const observer = new MutationObserver(onEvent);
        observer.observe(editorDom, { 
            attributes: true, 
            attributeFilter: ['style', 'class', 'width'], 
            subtree: true,
            childList: true // Catch when rows/cols are added/removed
        });
        
        // Initial check
        updateSticky();

        return () => {
            // eslint-disable-next-line no-restricted-syntax
            window.removeEventListener('scroll', onEvent);
            // eslint-disable-next-line no-restricted-syntax
            window.removeEventListener('resize', onEvent);
            document.removeEventListener('scroll', onEvent, true);
            observer.disconnect();
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [editor, topOffset]);

    if (!activeTable) return null;

    return createPortal(
        <div 
            className="doctex-rich-editor sticky-table-clone"
            style={{ 
                position: 'fixed', 
                top: activeTable.top, 
                left: activeTable.left, 
                width: activeTable.wrapperWidth, 
                overflow: 'hidden', 
                zIndex: 5, // Under fixed columns like .wf-column-index (z-index 10) and toolbars (15-40), but over text (0)
                pointerEvents: 'none', // Para que no intercepte clicks por accidente (solo lectura)
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
        >
            <div style={{ transform: `translateX(-${activeTable.scrollLeft}px)`, width: activeTable.tableWidth }}>
                <table style={{ tableLayout: 'fixed', width: activeTable.tableWidth, borderSpacing: 0, margin: 0, borderCollapse: 'separate' }}>
                    <tbody>
                        <tr>
                            {activeTable.ths.map((th, i) => (
                                <th 
                                    key={i}
                                    className={th.className}
                                    style={{ 
                                        width: th.width, 
                                        minWidth: th.width, 
                                        maxWidth: th.width,
                                        boxSizing: 'border-box',
                                        // Force borders for the clone to look good
                                        borderTop: '1px solid #4a4a4a',
                                        borderBottom: '1px solid #4a4a4a',
                                        borderLeft: i === 0 ? '1px solid #4a4a4a' : 'none',
                                        borderRight: '1px solid #4a4a4a',
                                        backgroundColor: '#2e2e2e',
                                        fontWeight: 600,
                                        padding: '8px 12px',
                                        textAlign: 'left'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: th.html }}
                                />
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>,
        document.body
    );
}
